import { api } from './api';

export enum ComplianceType {
    UPLOAD_DOC = 'upload_doc',
    COPY_PASTE = 'copy_paste',
    FORM = 'form'
}

// Nova estrutura alinhada com a resposta da API
interface ComplianceResponse {
    content: [{
        type: string;
        text: string;
        complianceForm?: {
            complianceFormSchema: {
                personalInformation: {
                    fullName: string;
                    birthDate: string;
                    nationality: string;
                };
                education: {
                    hasCompletedCollege: string;
                    institution: string;
                    degree: string;
                    fieldsOfStudy: string;
                    datesOfCompletion: string;
                    relevantContinuingEducation: string;
                };
                careerHistory: {
                    jobTitles: string;
                    employers: string;
                    durationInEachRole: string;
                    descriptionOfResponsibilities: string;
                    anySignificantCareer: string;
                };
                sourceOfWealth: {
                    professionalIncome: string;
                    ownsBusinessOwnership: string;
                    isEntrepreneur: string;
                    sectorActivity: string;
                    operateFinancial: string;
                    traderType: string;
                    hasInvestmentIncome: string;
                    realEstateInvestment: string;
                    privateEquityInvestment: string;
                    otherInvestment: string;
                    hasInheritedAssets: string;
                    valueInherited: string;
                    relationshipToDeceased: string;
                    nameOfDeceased: string;
                    soldCompanyProperty: string;
                    saleYear: string;
                    saleValue: string;
                    hasGovernmentContracts: string;
                    contractType: string;
                    publicEntityInvolved: string;
                    customerRole: string;
                    worksWithAgrobusiness: string;
                };
                financialInformation: {
                    totalNetWorth: string;
                    liquidNetWorth: string;
                    expectedAccountSize: string;
                    otherWealthSources: string;
                };
                pepStatus: {
                    holdsPoliticalOffice: string;
                    isGovernmentOfficial: string;
                    isJudge: string;
                    isMilitaryOfficer: string;
                    isStateCompanyExecutive: string;
                    isPoliticalPartyLeader: string;
                    hasRelationshipWithPEP: string;
                    isSpousePartner: string;
                    isFamily: string;
                    isBusinessAssociate: string;
                    hasGovernmentPosition: string;
                    positionTitle: string;
                    publicFigureName: string;
                    organizationName: string;
                    countryJurisdiction: string;
                    positionStartDate: string;
                    positionEndDate: string;
                };
            }
        }
    }];
    completed?: boolean;
}

export interface FileBufferInfo {
    buffer: ArrayBuffer;
    fileName: string;
    fileType: string;
}

/**
 * Envia os dados de compliance para o backend
 * @param type Tipo de compliance (obrigatório)
 * @param param1 Pode ser userId (string) ou file (File)
 * @param param2 Pode ser idOrganization (string) ou complianceText (string)
 * @param param3 Pode ser file (File), complianceText (string) ou objectForm (any)
 * @param param4 Pode ser complianceText (string) ou objectForm (any)
 * @param param5 Pode ser objectForm (any)
 * @returns Resposta do servidor
 */
export const submitCompliance = async (
    type: ComplianceType,
    param1?: string | File | ArrayBuffer | FileBufferInfo,
    param2?: string,
    param3?: File | string | ArrayBuffer | FileBufferInfo | any,
    param4?: string | any,
    param5?: any
): Promise<ComplianceResponse> => {
    const formData = new FormData();
    
    // Obter ID do usuário e da organização do localStorage
    let userId = '';
    let idOrganization = '';
    
    // Verifica se os primeiros parâmetros são strings (provavelmente userId e idOrganization)
    if (typeof param1 === 'string') {
        userId = param1;
        if (typeof param2 === 'string') {
            idOrganization = param2;
        }
    }
    
    // Se userId não foi fornecido, tenta obter do localStorage
    if (!userId) {
        const user = localStorage.getItem('@App:user');
        const userData = user ? JSON.parse(user) : null;
        
        if (!userData || !userData.id) {
            throw new Error('Usuário não autenticado');
        }
        
        userId = userData.id;
        
        // Verificar se o usuário tem organizationId
        if (!userData.organizationId) {
            throw new Error('Nenhuma organização disponível. Por favor, faça login novamente.');
        }
        
        // Usar a primeira organização disponível
        if (Array.isArray(userData.organizationId) && userData.organizationId.length > 0) {
            idOrganization = userData.organizationId[0];
        } else if (typeof userData.organizationId === 'string') {
            idOrganization = userData.organizationId;
        } else {
            throw new Error('Formato de organização inválido. Por favor, faça login novamente.');
        }
    }
    
    // Garantir que idOrganization esteja definido
    if (!idOrganization) {
        // Verificar se existe algum valor de organização no localStorage diretamente
        const organization = localStorage.getItem('@App:organization');
        if (organization) {
            try {
                const orgData = JSON.parse(organization);
                if (orgData && orgData.id) {
                    idOrganization = orgData.id;
                }
            } catch (error) {
                console.error('Erro ao parsear dados da organização:', error);
            }
        }
        
        // Se ainda não tiver um ID de organização, lançar erro
        if (!idOrganization) {
            throw new Error('ID da organização não encontrado');
        }
    }
    
    // Adicionar dados obrigatórios
    formData.append('userId', userId);
    formData.append('idOrganization', idOrganization);
    formData.append('type', type);
    
    // Adicionar modelo selecionado
    const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';
    formData.append('model', selectedModel);
    
    // Gerar um novo conversationId para esta submissão
    const conversationId = generateConversationId(idOrganization, userId);
    formData.append('conversationId', conversationId);
    
    // Identifica arquivo, texto e formulário com base no tipo dos parâmetros
    let file: File | ArrayBuffer | FileBufferInfo | undefined;
    let complianceText: string | undefined;
    let objectForm: any | undefined;
    
    if (param1 instanceof File || param1 instanceof ArrayBuffer || (typeof param1 === 'object' && param1 !== null && 'buffer' in param1)) {
        file = param1;
    }
    
    if (typeof param2 === 'string' && userId !== param2) {
        complianceText = param2;
    }
    
    if (param3) {
        if (param3 instanceof File || param3 instanceof ArrayBuffer || (typeof param3 === 'object' && param3 !== null && 'buffer' in param3)) {
            file = param3;
        } else if (typeof param3 === 'string') {
            complianceText = param3;
        } else {
            objectForm = param3;
        }
    }
    
    if (param4) {
        if (typeof param4 === 'string') {
            complianceText = param4;
        } else {
            objectForm = param4;
        }
    }
    
    if (param5) {
        objectForm = param5;
    }
    
    // Adicionar dados específicos conforme o tipo
    if (file) {
        if (file instanceof File) {
            formData.append('file', file);
        } else if (file instanceof ArrayBuffer) {
            // Converter ArrayBuffer para Blob e adicionar ao formData
            const blob = new Blob([file]);
            formData.append('file', blob, 'document.bin'); // Nome de arquivo padrão
        } else if (typeof file === 'object' && 'buffer' in file) {
            // É um FileBufferInfo
            const { buffer, fileName, fileType } = file;
            const blob = new Blob([buffer], { type: fileType });
            formData.append('file', blob, fileName);
        }
    }
    
    if (complianceText) {
        formData.append('complianceText', complianceText);
    }
    
    // Se for do tipo FORM, adiciona o objectForm no formato esperado pela API
    if (type === ComplianceType.FORM && objectForm) {
        // Função auxiliar para verificar e substituir campos vazios por "unknown"
        const setDefaultIfEmpty = (value: any) => {
            // Retorna "unknown" para valores vazios, undefined, null ou strings vazias
            if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
                return "unknown";
            }
            return value;
        };

        const formattedObject = {
            content: [
                {
                    type: "text",
                    text: "Processando formulário para análise de compliance",
                    complianceForm: {
                        complianceFormSchema: {
                            personalInformation: {
                                fullName: setDefaultIfEmpty(objectForm.fullName),
                                birthDate: setDefaultIfEmpty(objectForm.birthDate),
                                nationality: setDefaultIfEmpty(objectForm.nationality)
                            },
                            education: {
                                hasCompletedCollege: setDefaultIfEmpty(objectForm.hasCompletedCollege),
                                institution: setDefaultIfEmpty(objectForm.institution),
                                degree: setDefaultIfEmpty(objectForm.degree),
                                fieldsOfStudy: setDefaultIfEmpty(objectForm.fieldsOfStudy),
                                datesOfCompletion: setDefaultIfEmpty(objectForm.datesOfCompletion),
                                relevantContinuingEducation: setDefaultIfEmpty(objectForm.relevantContinuingEducation)
                            },
                            careerHistory: {
                                jobTitles: setDefaultIfEmpty(objectForm.jobTitles),
                                employers: setDefaultIfEmpty(objectForm.employers),
                                durationInEachRole: setDefaultIfEmpty(objectForm.durationInEachRole),
                                descriptionOfResponsibilities: setDefaultIfEmpty(objectForm.descriptionOfResponsibilities),
                                anySignificantCareer: setDefaultIfEmpty(objectForm.anySignificantCareer)
                            },
                            sourceOfWealth: {
                                professionalIncome: setDefaultIfEmpty(objectForm.professionalIncome),
                                ownsBusinessOwnership: setDefaultIfEmpty(objectForm.ownsBusinessOwnership),
                                isEntrepreneur: setDefaultIfEmpty(objectForm.isEntrepreneur),
                                sectorActivity: setDefaultIfEmpty(objectForm.sectorActivity),
                                operateFinancial: setDefaultIfEmpty(objectForm.operateFinancial),
                                traderType: setDefaultIfEmpty(objectForm.traderType),
                                hasInvestmentIncome: setDefaultIfEmpty(objectForm.hasInvestmentIncome),
                                realEstateInvestment: setDefaultIfEmpty(objectForm.realEstateInvestment),
                                privateEquityInvestment: setDefaultIfEmpty(objectForm.privateEquityInvestment),
                                otherInvestment: setDefaultIfEmpty(objectForm.otherInvestment),
                                hasInheritedAssets: setDefaultIfEmpty(objectForm.hasInheritedAssets),
                                valueInherited: setDefaultIfEmpty(objectForm.valueInherited),
                                relationshipToDeceased: setDefaultIfEmpty(objectForm.relationshipToDeceased),
                                nameOfDeceased: setDefaultIfEmpty(objectForm.nameOfDeceased),
                                soldCompanyProperty: setDefaultIfEmpty(objectForm.soldCompanyProperty),
                                saleYear: setDefaultIfEmpty(objectForm.saleYear),
                                saleValue: setDefaultIfEmpty(objectForm.saleValue),
                                hasGovernmentContracts: setDefaultIfEmpty(objectForm.hasGovernmentContracts),
                                contractType: setDefaultIfEmpty(objectForm.contractType),
                                publicEntityInvolved: setDefaultIfEmpty(objectForm.publicEntityInvolved),
                                customerRole: setDefaultIfEmpty(objectForm.customerRole),
                                worksWithAgrobusiness: setDefaultIfEmpty(objectForm.worksWithAgrobusiness)
                            },
                            financialInformation: {
                                totalNetWorth: setDefaultIfEmpty(objectForm.totalNetWorth),
                                liquidNetWorth: setDefaultIfEmpty(objectForm.liquidNetWorth),
                                expectedAccountSize: setDefaultIfEmpty(objectForm.expectedAccountSize),
                                otherWealthSources: setDefaultIfEmpty(objectForm.otherWealthSources)
                            },
                            pepStatus: {
                                holdsPoliticalOffice: setDefaultIfEmpty(objectForm.holdsPoliticalOffice),
                                isGovernmentOfficial: setDefaultIfEmpty(objectForm.isGovernmentOfficial),
                                isJudge: setDefaultIfEmpty(objectForm.isJudge),
                                isMilitaryOfficer: setDefaultIfEmpty(objectForm.isMilitaryOfficer),
                                isStateCompanyExecutive: setDefaultIfEmpty(objectForm.isStateCompanyExecutive),
                                isPoliticalPartyLeader: setDefaultIfEmpty(objectForm.isPoliticalPartyLeader),
                                hasRelationshipWithPEP: setDefaultIfEmpty(objectForm.hasRelationshipWithPEP),
                                isSpousePartner: setDefaultIfEmpty(objectForm.isSpousePartner),
                                isFamily: setDefaultIfEmpty(objectForm.isFamily),
                                isBusinessAssociate: setDefaultIfEmpty(objectForm.isBusinessAssociate),
                                hasGovernmentPosition: setDefaultIfEmpty(objectForm.hasGovernmentPosition),
                                positionTitle: setDefaultIfEmpty(objectForm.positionTitle),
                                publicFigureName: setDefaultIfEmpty(objectForm.publicFigureName),
                                organizationName: setDefaultIfEmpty(objectForm.organizationName),
                                countryJurisdiction: setDefaultIfEmpty(objectForm.countryJurisdiction),
                                positionStartDate: setDefaultIfEmpty(objectForm.positionStartDate),
                                positionEndDate: setDefaultIfEmpty(objectForm.positionEndDate)
                            }
                        }
                    }
                }
            ]
        };
        
        formData.append('objectForm', JSON.stringify(formattedObject));
    } 
    // Para tipos UPLOAD_DOC e COPY_PASTE, estrutura o payload no formato esperado
    else {
        const requestPayload = {
            content: [
                {
                    type: "text",
                    text: type === ComplianceType.UPLOAD_DOC 
                        ? "Processando arquivo para análise de compliance"
                        : "Processando texto para análise de compliance",
                    complianceForm: {
                        complianceFormSchema: {
                            personalInformation: {
                                fullName: '',
                                birthDate: '',
                                nationality: 'Brazilian'
                            },
                            education: {
                                hasCompletedCollege: 'no',
                                institution: '',
                                degree: '',
                                fieldsOfStudy: '',
                                datesOfCompletion: '',
                                relevantContinuingEducation: ''
                            },
                            careerHistory: {
                                jobTitles: '',
                                employers: '',
                                durationInEachRole: '',
                                descriptionOfResponsibilities: '',
                                anySignificantCareer: ''
                            },
                            sourceOfWealth: {
                                professionalIncome: '',
                                ownsBusinessOwnership: '',
                                isEntrepreneur: '',
                                sectorActivity: '',
                                operateFinancial: '',
                                traderType: '',
                                hasInvestmentIncome: '',
                                realEstateInvestment: '',
                                privateEquityInvestment: '',
                                otherInvestment: '',
                                hasInheritedAssets: '',
                                valueInherited: '',
                                relationshipToDeceased: '',
                                nameOfDeceased: '',
                                soldCompanyProperty: '',
                                saleYear: '',
                                saleValue: '',
                                hasGovernmentContracts: '',
                                contractType: '',
                                publicEntityInvolved: '',
                                customerRole: '',
                                worksWithAgrobusiness: ''
                            },
                            financialInformation: {
                                totalNetWorth: '',
                                liquidNetWorth: '',
                                expectedAccountSize: '',
                                otherWealthSources: ''
                            },
                            pepStatus: {
                                holdsPoliticalOffice: '',
                                isGovernmentOfficial: '',
                                isJudge: '',
                                isMilitaryOfficer: '',
                                isStateCompanyExecutive: '',
                                isPoliticalPartyLeader: '',
                                hasRelationshipWithPEP: '',
                                isSpousePartner: '',
                                isFamily: '',
                                isBusinessAssociate: '',
                                hasGovernmentPosition: '',
                                positionTitle: '',
                                publicFigureName: '',
                                organizationName: '',
                                countryJurisdiction: '',
                                positionStartDate: '',
                                positionEndDate: ''
                            }
                        }
                    }
                }
            ]
        };
        
        formData.append('requestPayload', JSON.stringify(requestPayload));
    }
    
    // Fazer a requisição
    const response = await api.post<ComplianceResponse>('/api/compliance', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    
    // Adicionar campo completed se não existir
    if (response.data && response.data.completed === undefined) {
        response.data.completed = true;
    }
    
    return response.data;
};

// Função para gerar um novo conversationId com base nos IDs de usuário e organização
export const generateConversationId = (organizationId: string, userId: string): string => {
    // Verifica se já existe um conversationId armazenado para esta conversa
    const storedConversationId = localStorage.getItem('currentConversationId');
    
    if (storedConversationId) {
        try {
            // O conversationId tem formato orgId_userId_timestamp
            const parts = storedConversationId.split('_');
            if (parts.length === 3) {
                const storedOrgId = parts[0];
                const storedUserId = parts[1];
                const timestamp = parseInt(parts[2]);
                
                // Verifica se o conversationId pertence ao mesmo usuário e organização
                if (storedOrgId === organizationId && storedUserId === userId) {
                    // Verifica se o conversationId foi gerado nas últimas 24 horas
                    const now = new Date().getTime();
                    const oneDay = 24 * 60 * 60 * 1000; // 24 horas em milissegundos
                    
                    if (now - timestamp < oneDay) {
                        console.log('Reutilizando conversationId existente:', storedConversationId);
                        return storedConversationId;
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao processar conversationId armazenado:', error);
        }
    }
    
    // Se não houver um conversationId válido armazenado, gerar um novo
    const newConversationId = `${organizationId}_${userId}_${new Date().getTime()}`;
    
    // Armazenar o novo conversationId para uso futuro
    localStorage.setItem('currentConversationId', newConversationId);
    console.log('Novo conversationId gerado:', newConversationId);
    
    return newConversationId;
};

// Função para limpar o conversationId atual e forçar a geração de um novo
export const clearCurrentConversationId = (): void => {
    localStorage.removeItem('currentConversationId');
    console.log('ConversationId limpo, próxima conversa terá um novo ID');
};

export const saveComplianceResult = async (idOrganization: string, userId: string, complianceText: string, apiResponseFromFirstCall?: any, conversationId?: string) => {
  try {
    const token = localStorage.getItem('@App:token');

    console.log('saveComplianceResult - Parâmetros iniciais:', {
      idOrganization,
      userId,
      apiResponsePresente: !!apiResponseFromFirstCall,
      conversationId
    });

    // Verificar se idOrganization está vazio e tentar obtê-lo da resposta da primeira chamada
    let finalIdOrganization = idOrganization;
    
    if (!finalIdOrganization && apiResponseFromFirstCall) {
      console.log('Tentando obter idOrganization da resposta da API:', apiResponseFromFirstCall);
      
      // Tenta extrair o idOrganization da resposta da primeira chamada à API
      if (apiResponseFromFirstCall.idOrganization) {
        finalIdOrganization = apiResponseFromFirstCall.idOrganization;
        console.log('ID da organização obtido da primeira chamada (idOrganization):', finalIdOrganization);
      } 
      // Verifica se o idOrganization está em data.idOrganization (se apiResponseFromFirstCall for resposta do axios)
      else if (apiResponseFromFirstCall.data && apiResponseFromFirstCall.data.idOrganization) {
        finalIdOrganization = apiResponseFromFirstCall.data.idOrganization;
        console.log('ID da organização obtido da primeira chamada (data.idOrganization):', finalIdOrganization);
      }
      // Verifica diretamente do formulário (caso seja FormData processado)
      else if (apiResponseFromFirstCall.get && typeof apiResponseFromFirstCall.get === 'function') {
        try {
          const formIdOrg = apiResponseFromFirstCall.get('idOrganization');
          if (formIdOrg) {
            finalIdOrganization = formIdOrg;
            console.log('ID da organização obtido do FormData:', finalIdOrganization);
          }
        } catch (e) {
          console.log('Erro ao tentar obter idOrganization do FormData:', e);
        }
      }
    }
    
    // Se ainda não tiver ID da organização, buscar no localStorage como fallback
    if (!finalIdOrganization) {
      const organization = localStorage.getItem('@App:organization');
      console.log('Valor bruto de @App:organization no localStorage:', organization);
      
      if (organization) {
        try {
          const orgData = JSON.parse(organization);
          console.log('Dados de organização parseados:', orgData);
          
          if (orgData && orgData.id) {
            finalIdOrganization = orgData.id;
            console.log('ID da organização obtido do localStorage (@App:organization):', finalIdOrganization);
          }
        } catch (error) {
          console.error('Erro ao parsear dados da organização do localStorage:', error);
        }
      }
    }
    
    // Tentar obter do usuário logado como fallback adicional
    if (!finalIdOrganization) {
      const user = localStorage.getItem('@App:user');
      console.log('Valor bruto de @App:user no localStorage:', user);
      
      if (user) {
        try {
          const userData = JSON.parse(user);
          console.log('Dados do usuário parseados:', userData);
          
          if (userData && userData.organizationId) {
            // Se organizationId for um array, usar o primeiro elemento
            if (Array.isArray(userData.organizationId) && userData.organizationId.length > 0) {
              finalIdOrganization = userData.organizationId[0];
            } 
            // Se for string, usar diretamente
            else if (typeof userData.organizationId === 'string') {
              finalIdOrganization = userData.organizationId;
            }
            console.log('ID da organização obtido do usuário logado:', finalIdOrganization);
          }
        } catch (error) {
          console.error('Erro ao parsear dados do usuário do localStorage:', error);
        }
      }
    }
    
    // Verificar se ainda está sem ID de organização
    if (!finalIdOrganization) {
      console.error('ID da organização não encontrado para a chamada /api/compliance/results - Todas as tentativas falharam');
      throw new Error('ID da organização não encontrado');
    }

    console.log('Enviando dados para /api/compliance/results com idOrganization:', finalIdOrganization);
    
    // Se não tiver um conversationId, gerar um novo
    const finalConversationId = conversationId || generateConversationId(finalIdOrganization, userId);
    
    const response = await api.post('/api/compliance/results', {
      idOrganization: finalIdOrganization,
      userId,
      complianceText,
      conversationId: finalConversationId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('Resposta completa da API /api/compliance/results:', response);
    
    return response.data;
  } catch (error) {
    console.error('Erro ao salvar resultado de compliance:', error);
    throw error;
  }
}; 